import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import financialsService, {
  BudgetSummary,
  CashFlowPoint,
  PaymentSummary,
  VarianceRow,
} from '../../services/api/financialsService';
import kpiService, { DashboardData, KPI } from '../../services/api/kpiService';
import procurementService, { GoodsReceipt, PurchaseOrder } from '../../services/api/procurementService';
import inventoryMovementService, { InventoryMovementItem } from '../../services/api/inventoryMovementService';
import resourceService, { InventorySummaryRow } from '../../services/api/resourceService';
import teamService, { TeamOverviewRow } from '../../services/api/teamService';
import { Task } from '../../services/api/tasksService';
import accountsTransactionService from '../../services/api/accountsTransactionService';
import { AccountsTransaction } from '../../services/api/roleWorkspaceService';
import { loadAllProjectTasks } from './supervisorFieldData';

interface ExecutiveFieldData {
  projectId: string | null;
  projectName: string;
  projectsLoading: boolean;
  projectsError: string | null;
  dashboard: DashboardData | null;
  tasks: Task[];
  teams: TeamOverviewRow[];
  purchaseOrders: PurchaseOrder[];
  goodsReceipts: GoodsReceipt[];
  inventory: InventorySummaryRow[];
  inventoryMovements: InventoryMovementItem[];
  paymentSummary: PaymentSummary | null;
  accountsTransactions: AccountsTransaction[];
  budgetSummary: BudgetSummary | null;
  cashFlow: CashFlowPoint[];
  variances: VarianceRow[];
  loading: boolean;
  /** Set when every data source failed. */
  error: string | null;
  /** Sources that failed while others loaded (partial outage). */
  failedSources: string[];
  reload: () => void;
}

const firstFulfilled = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
  result.status === 'fulfilled' ? result.value : fallback;

export const kpiByName = (dashboard: DashboardData | null, name: string): KPI | null =>
  dashboard?.kpis?.find((kpi) => kpi.name === name) ?? null;

export default function useExecutiveFieldData(): ExecutiveFieldData {
  const {
    activeProjectId,
    projects,
    loading: projectsLoading,
    error: projectsError,
  } = useSelector((state: RootState) => state.projects);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [teams, setTeams] = useState<TeamOverviewRow[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [goodsReceipts, setGoodsReceipts] = useState<GoodsReceipt[]>([]);
  const [inventory, setInventory] = useState<InventorySummaryRow[]>([]);
  const [inventoryMovements, setInventoryMovements] = useState<InventoryMovementItem[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [accountsTransactions, setAccountsTransactions] = useState<AccountsTransaction[]>([]);
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowPoint[]>([]);
  const [variances, setVariances] = useState<VarianceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  const projectName = projects.find((project) => project.id === activeProjectId)?.name ?? 'Active project';

  useEffect(() => {
    if (!activeProjectId) {
      setDashboard(null);
      setTasks([]);
      setTeams([]);
      setPurchaseOrders([]);
      setGoodsReceipts([]);
      setInventory([]);
      setInventoryMovements([]);
      setPaymentSummary(null);
      setAccountsTransactions([]);
      setBudgetSummary(null);
      setCashFlow([]);
      setVariances([]);
      setLoading(false);
      setError(null);
      setFailedSources([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const results = await Promise.allSettled([
        kpiService.getDashboardData(),
        loadAllProjectTasks(activeProjectId),
        teamService.getOverview(activeProjectId),
        procurementService.listPurchaseOrders({ projectId: activeProjectId, limit: 200 }),
        procurementService.listGoodsReceipts({ projectId: activeProjectId, limit: 500 }),
        resourceService.getInventorySummary(),
        inventoryMovementService.getMovements({ projectId: activeProjectId, limit: 500 }),
        financialsService.getPaymentSummary(activeProjectId),
        accountsTransactionService.list(activeProjectId),
        financialsService.getBudgetSummary(activeProjectId),
        financialsService.getCashFlowComparison(activeProjectId, 6),
        financialsService.listVariances(activeProjectId),
      ] as const);
      if (cancelled) return;

      const [
        dashboardResult,
        tasksResult,
        teamsResult,
        poResult,
        grnResult,
        inventoryResult,
        movementsResult,
        paymentsResult,
        accountsTransactionsResult,
        budgetResult,
        cashFlowResult,
        variancesResult,
      ] = results;

      setDashboard(firstFulfilled(dashboardResult, null));
      setTasks(firstFulfilled(tasksResult, []));
      setTeams(firstFulfilled(teamsResult, []));
      setPurchaseOrders(firstFulfilled(poResult, { rows: [], total: 0, page: 1, limit: 200, totalPages: 1 }).rows);
      setGoodsReceipts(firstFulfilled(grnResult, { rows: [], total: 0, page: 1, limit: 500, totalPages: 1 }).rows);
      setInventory(firstFulfilled(inventoryResult, []));
      setInventoryMovements(firstFulfilled(movementsResult, []));
      setPaymentSummary(firstFulfilled(paymentsResult, null));
      setAccountsTransactions(firstFulfilled(accountsTransactionsResult, []));
      setBudgetSummary(firstFulfilled(budgetResult, null));
      setCashFlow(firstFulfilled(cashFlowResult, []));
      setVariances(firstFulfilled(variancesResult, []));

      const sourceNames = [
        'executive dashboard',
        'tasks',
        'teams',
        'purchase orders',
        'goods receipts',
        'inventory',
        'inventory movements',
        'payments',
        'accounts transactions',
        'budget',
        'cash flow',
        'variances',
      ];
      const failed = results
        .map((result, index) => (result.status === 'rejected' ? sourceNames[index] : null))
        .filter((name): name is string => name !== null);
      setFailedSources(failed);
      if (failed.length === results.length) {
        const firstFailure = results.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined;
        setError(
          (firstFailure?.reason as any)?.response?.data?.message
          || 'Executive data could not be loaded.'
        );
      }
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [activeProjectId, reloadToken]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  return {
    projectId: activeProjectId,
    projectName,
    projectsLoading,
    projectsError,
    dashboard,
    tasks,
    teams,
    purchaseOrders,
    goodsReceipts,
    inventory,
    inventoryMovements,
    paymentSummary,
    accountsTransactions,
    budgetSummary,
    cashFlow,
    variances,
    loading,
    error,
    failedSources,
    reload,
  };
}
